import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Trash2, AlertCircle, Calendar, Target } from 'lucide-react';
import { CreateWorkoutPlanData, CreatePlanWorkoutData, WorkoutWithBlocks } from '../types/dataModel';
import { WorkoutService } from '../lib/workoutService';

interface CreateWorkoutPlanFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (planData: CreateWorkoutPlanData) => Promise<void>;
  existingPlan?: any; // For editing existing plans
}

export const CreateWorkoutPlanForm: React.FC<CreateWorkoutPlanFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingPlan
}) => {
  const navigate = useNavigate();
  interface PlanFormData extends CreateWorkoutPlanData {
    goals: string[];
    workoutAssignments: Array<{
      workoutId: string;
      weekNumber: number;
      dayNumber: number;
      workoutOrder: number;
      notes?: string;
    }>;
  }

  const [formData, setFormData] = useState<PlanFormData>({
    name: '',
    description: '',
    difficulty: 'beginner',
    totalNoOfDays: 28, // durationWeeks → totalNoOfDays (4 weeks = 28 days)
    goals: [''],
    isPublic: false,
    workoutAssignments: []
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWorkoutSelector, setShowWorkoutSelector] = useState(false);
  const [availableWorkouts, setAvailableWorkouts] = useState<WorkoutWithBlocks[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');

  // Temporary: Using simplified structure
  useEffect(() => {
    if (existingPlan) {
      setFormData({
        name: existingPlan.name || '',
        description: existingPlan.description || '',
        difficulty: existingPlan.difficulty || 'beginner',
        totalNoOfDays: existingPlan.totalNoOfDays || 28, // durationWeeks → totalNoOfDays
        goals: existingPlan.goals || [''],
        isPublic: existingPlan.isPublic || false,
        workoutAssignments: existingPlan.workoutAssignments || []
      });
    }
  }, [existingPlan]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      loadAvailableWorkouts();
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const loadAvailableWorkouts = async () => {
    try {
      const workouts = await WorkoutService.getWorkouts();
      setAvailableWorkouts(workouts);
    } catch (error) {
      console.error('Error loading workouts:', error);
    }
  };

  const checkWorkoutNameExists = (name: string, excludeWorkoutId?: string): boolean => {
    const trimmed = (name || '').trim().toLowerCase();
    if (!trimmed) return false;
    return availableWorkouts.some(
      (w) => w.id !== excludeWorkoutId && String(w.name || '').trim().toLowerCase() === trimmed
    );
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Plan name must be 255 characters or less';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be 1000 characters or less';
    }

    if (!formData.goals || formData.goals.length === 0 || (formData.goals.length === 1 && !formData.goals[0].trim())) {
      newErrors.goals = 'At least one goal is required';
    }

    if (!formData.workoutAssignments || formData.workoutAssignments.length === 0) {
      newErrors.workoutAssignments = 'At least one workout assignment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error creating workout plan:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addGoal = () => {
    setFormData((prev: PlanFormData) => ({
      ...prev,
      goals: [...(prev.goals || []), '']
    }));
  };

  const removeGoal = (index: number) => {
    setFormData((prev: PlanFormData) => ({
      ...prev,
      goals: (prev.goals || []).filter((_: string, i: number) => i !== index)
    }));
  };

  const updateGoal = (index: number, value: string) => {
    setFormData((prev: PlanFormData) => ({
      ...prev,
      goals: (prev.goals || []).map((goal: string, i: number) => i === index ? value : goal)
    }));
  };

  const addWorkoutAssignment = (workout: WorkoutWithBlocks) => {
    const newAssignment = {
      workoutPlanId: '', // Will be set when plan is created
      workoutId: workout.id,
      weekNumber: 1,
      dayNumber: 1,
      workoutOrder: (formData.workoutAssignments?.length || 0) + 1,
      notes: ''
    };

    setFormData((prev: PlanFormData) => ({
      ...prev,
      workoutAssignments: [...(prev.workoutAssignments || []), newAssignment]
    }));

    setShowWorkoutSelector(false);
  };

  const handleCreateWorkoutFromSelector = () => {
    navigate('/trainer-dashboard/workout/new');
  };

  const removeWorkoutAssignment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      workoutAssignments: prev.workoutAssignments.filter((_: any, i: number) => i !== index)
    }));
  };

  const updateWorkoutAssignment = (index: number, field: string, value: any) => {
    setFormData((prev: PlanFormData) => ({
      ...prev,
      workoutAssignments: (prev.workoutAssignments || []).map((assignment: any, i: number) =>
        i === index ? { ...assignment, [field]: value } : assignment
      )
    }));
  };

  const getWorkoutById = (workoutId: string): WorkoutWithBlocks | undefined => {
    return availableWorkouts.find(w => w.id === workoutId);
  };

  const getDayName = (dayNumber: number): string => {
    const days = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNumber] || '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-2xl font-semibold text-gray-900">
            {existingPlan ? 'Edit Workout Plan' : 'Create New Workout Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form - Scrollable Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1 modal-content">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev: PlanFormData) => ({ ...prev, name: e.target.value }))}
                maxLength={255}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., 12-Week Strength Program"
              />
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-500">
                  {formData.name.length}/255 characters
                </span>
                {formData.name.length > 200 && (
                  <span className="text-xs text-orange-500">
                    Getting close to limit
                  </span>
                )}
              </div>
              {errors.name && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty *
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev: PlanFormData) => ({ ...prev, description: e.target.value }))}
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Describe the overall program, goals, and any special considerations..."
            />
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-500">
                {(formData.description || '').length}/1000 characters
              </span>
              {(formData.description || '').length > 800 && (
                <span className="text-xs text-orange-500">
                  Getting close to limit
                </span>
              )}
            </div>
            {errors.description && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (weeks) *
              </label>
              <input
                type="number"
                min="1"
                max="52"
                value={formData.totalNoOfDays}
                onChange={(e) => setFormData((prev: PlanFormData) => ({ ...prev, totalNoOfDays: parseInt(e.target.value) || 28 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPublic}
                  onChange={(e) => setFormData((prev: PlanFormData) => ({ ...prev, isPublic: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Make this plan public</span>
              </label>
            </div>
          </div>

          {/* Goals Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Program Goals
              </h3>
              <button
                type="button"
                onClick={addGoal}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-600 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Goal
              </button>
            </div>

            {errors.goals && (
              <p className="text-red-500 text-sm mb-4 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.goals}
              </p>
            )}

            <div className="space-y-3">
              {(formData.goals || []).map((goal: string, index: number) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => updateGoal(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Build strength, Improve endurance..."
                  />
                  <button
                    type="button"
                    onClick={() => removeGoal(index)}
                    className="p-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Workout Assignments Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Workout Schedule
              </h3>
              <button
                type="button"
                onClick={() => setShowWorkoutSelector(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-[var(--ink)] bg-[var(--action-primary)] hover:bg-[var(--action-primary-pressed)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Workout
              </button>
            </div>

            {errors.workoutAssignments && (
              <p className="text-red-500 text-sm mb-4 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.workoutAssignments}
              </p>
            )}

            {(!formData.workoutAssignments || formData.workoutAssignments.length === 0) ? (
              <div className="text-center py-8 text-gray-500">
                <p>No workouts scheduled yet. Click "Add Workout" to create your training schedule.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(formData.workoutAssignments || []).map((assignment: any, index: number) => {
                  const workout = getWorkoutById(assignment.workoutId);
                  return (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{workout?.name || 'Unknown Workout'}</h4>
                          <p className="text-sm text-gray-600">{workout?.note || 'No description available'}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeWorkoutAssignment(index)}
                          className="p-1 text-red-400 hover:text-red-600 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Week</label>
                          <input
                            type="number"
                            min="1"
                            max={formData.totalNoOfDays}
                            value={assignment.weekNumber}
                            onChange={(e) => updateWorkoutAssignment(index, 'weekNumber', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Day</label>
                          <select
                            value={assignment.dayNumber}
                            onChange={(e) => updateWorkoutAssignment(index, 'dayNumber', parseInt(e.target.value))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            {[1, 2, 3, 4, 5, 6, 7].map(day => (
                              <option key={day} value={day}>
                                {getDayName(day)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Order</label>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={assignment.workoutOrder}
                            onChange={(e) => updateWorkoutAssignment(index, 'workoutOrder', parseInt(e.target.value) || 1)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Difficulty</label>
                          <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(workout?.difficulty || 'beginner')}`}>
                            {workout?.difficulty || 'beginner'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          value={assignment.notes || ''}
                          onChange={(e) => updateWorkoutAssignment(index, 'notes', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Any special instructions or modifications..."
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[var(--action-primary)] text-[var(--ink)] rounded-md hover:bg-[var(--action-primary-pressed)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating...' : (existingPlan ? 'Update Workout Plan' : 'Create Workout Plan')}
            </button>
          </div>
        </form>
      </div>

      {/* Workout Selector Modal */}
      {showWorkoutSelector && (
        <WorkoutSelector
          onClose={() => setShowWorkoutSelector(false)}
          onWorkoutSelect={addWorkoutAssignment}
          onCreateWorkout={handleCreateWorkoutFromSelector}
          availableWorkouts={availableWorkouts}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedDifficulty={selectedDifficulty}
          setSelectedDifficulty={setSelectedDifficulty}
        />
      )}

    </div>
  );
};

// Workout Selector Component
interface WorkoutSelectorProps {
  onClose: () => void;
  onWorkoutSelect: (workout: WorkoutWithBlocks) => void;
  onCreateWorkout: () => void;
  availableWorkouts: WorkoutWithBlocks[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedDifficulty: string;
  setSelectedDifficulty: (difficulty: string) => void;
}

export const WorkoutSelector: React.FC<WorkoutSelectorProps> = ({
  onClose,
  onWorkoutSelect,
  onCreateWorkout,
  availableWorkouts,
  searchTerm,
  setSearchTerm,
  selectedDifficulty,
  setSelectedDifficulty
}) => {
  const filteredWorkouts = availableWorkouts.filter(workout => {
    const matchesSearch = workout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (workout.note || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = selectedDifficulty === 'all' || (workout.difficulty || 'beginner') === selectedDifficulty;
    return matchesSearch && matchesDifficulty;
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-semibold text-gray-900">Select Workout</h3>
            <button
              type="button"
              onClick={onCreateWorkout}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-[var(--ink)] bg-[var(--action-primary)] hover:bg-[var(--action-primary-pressed)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create workout
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 modal-content">
          {/* Filters */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <input
              type="text"
              placeholder="Search workouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Difficulties</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Workout List */}
          <div className="max-h-96 overflow-y-auto">
            <div className="grid gap-3">
              {filteredWorkouts.map((workout) => (
                <div
                  key={workout.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onWorkoutSelect(workout)}
                >
                  <h4 className="font-medium text-gray-900">{workout.name}</h4>
                  <p className="text-sm text-gray-600 line-clamp-2">{workout.note || ''}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(workout.difficulty || 'beginner')}`}>
                      {workout.difficulty || 'beginner'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {workout.workoutBlocks?.reduce((total, block) => total + (block.exerciseSets?.length || 0), 0) || 0} exercises
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function for difficulty colors
const getDifficultyColor = (difficulty: string): string => {
  switch (difficulty) {
    case 'beginner':
      return 'bg-green-100 text-green-800';
    case 'intermediate':
      return 'bg-yellow-100 text-yellow-800';
    case 'advanced':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
